import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DOMAINS } from './GlobeMap';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

const ESCALATION_COLORS = { peace:'#1a1a2e', economic_competition:'#2d6a4f', clandestine:'#52b788', covert:'#ffcc33', overt:'#ff9933', limited_conventional:'#e63946', lsco:'#c1121f', global_strategic:'#7b2d8b', nuclear_chemical:'#3d0000' };
const ESCALATION_LABELS = { peace:'Peace', economic_competition:'Econ Competition', clandestine:'Clandestine', covert:'Covert', overt:'Overt', limited_conventional:'Ltd Conventional', lsco:'LSCO', global_strategic:'Global Strategic', nuclear_chemical:'Nuclear/Chemical' };
const levelColor = (l) => ESCALATION_COLORS[l] || '#c4a563';
const levelLabel = (l) => ESCALATION_LABELS[l] || (l ? l.charAt(0).toUpperCase()+l.slice(1) : 'Unknown');


const mockEvents = () => [
  { id:1, icon:'⚔️', label:'Military',    domainKey:'D2', title:'Elevated readiness posture observed',  date:'30 May', ci:82, offsetLat:2,  offsetLon:3  },
  { id:2, icon:'⚓',  label:'Maritime',    domainKey:'D3', title:'Sanctions enforcement action reported', date:'29 May', ci:74, offsetLat:-2, offsetLon:-3 },
  { id:3, icon:'📡', label:'Information', domainKey:'D6', title:'Bilateral talks suspended',             date:'28 May', ci:91, offsetLat:3,  offsetLon:-2 },
];

const FactRow = ({ label, value, color }) => (
  <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
    <span style={{ color:'#555', fontSize:'12px' }}>{label}</span>
    <span style={{ color:color||'#c4a563', fontSize:'12px', fontWeight:'bold', textAlign:'right', maxWidth:'60%' }}>{String(value)}</span>
  </div>
);

const fmt = {
  population:                    v => v ? (v/1e6).toFixed(1)+' M' : '—',
  gdp_usd:                       v => v ? '$'+(v/1e12).toFixed(2)+' T' : '—',
  gdp_per_capita:                v => v ? '$'+Math.round(v).toLocaleString() : '—',
  gdp_ppp:                       v => v ? '$'+(v/1e12).toFixed(2)+' T PPP' : '—',
  gdp_growth:                    v => v ? v.toFixed(1)+'%' : '—',
  inflation:                     v => v ? v.toFixed(1)+'%' : '—',
  unemployment:                  v => v ? v.toFixed(1)+'%' : '—',
  exports_usd:                   v => v ? '$'+(v/1e9).toFixed(1)+' B' : '—',
  imports_usd:                   v => v ? '$'+(v/1e9).toFixed(1)+' B' : '—',
  fdi_inflows:                   v => v ? '$'+(v/1e9).toFixed(1)+' B' : '—',
  external_debt:                 v => v ? '$'+(v/1e9).toFixed(1)+' B' : '—',
  reserves:                      v => v ? '$'+(v/1e9).toFixed(1)+' B' : '—',
  current_account:               v => v ? '$'+(v/1e9).toFixed(1)+' B' : '—',
  gini_index:                    v => v ? v.toFixed(1) : '—',
  poverty_rate:                  v => v ? v.toFixed(1)+'%' : '—',
  internet_users:                v => v ? v.toFixed(1)+'%' : '—',
  life_expectancy:               v => v ? v.toFixed(1)+' yrs' : '—',
  fertility_rate:                v => v ? v.toFixed(2) : '—',
  infant_mortality:              v => v ? v.toFixed(1)+' per 1K' : '—',
  population_growth:             v => v ? v.toFixed(2)+'%' : '—',
  urbanization:                  v => v ? v.toFixed(1)+'%' : '—',
  literacy_rate:                 v => v ? v.toFixed(1)+'%' : '—',
  pop_0_14:                      v => v ? v.toFixed(1)+'%' : '—',
  pop_65_plus:                   v => v ? v.toFixed(1)+'%' : '—',
  median_age:                    v => v ? v.toFixed(1) : '—',
  refugee_population:            v => v ? Math.round(v).toLocaleString() : '—',
  military_expenditure_usd:      v => v ? '$'+(v/1e9).toFixed(1)+' B' : '—',
  military_expenditure_pct_gdp:  v => v ? v.toFixed(2)+'%' : '—',
  military_expenditure_pct_govt: v => v ? v.toFixed(2)+'%' : '—',
  area_sq_km:                    v => v ? Math.round(v).toLocaleString()+' km²' : '—',
  coastline_km:                  v => v ? Math.round(v).toLocaleString()+' km' : '—',
  land_boundaries_km:            v => v ? Math.round(v).toLocaleString()+' km' : '—',
};

const LABELS = {
  population:'Population', gdp_usd:'GDP', gdp_per_capita:'GDP per Capita',
  gdp_ppp:'GDP (PPP)', gdp_growth:'GDP Growth', inflation:'Inflation',
  unemployment:'Unemployment', exports_usd:'Exports', imports_usd:'Imports',
  fdi_inflows:'FDI Inflows', external_debt:'External Debt', reserves:'Reserves',
  current_account:'Current Account', gini_index:'Gini Index', poverty_rate:'Poverty Rate',
  internet_users:'Internet Users', life_expectancy:'Life Expectancy',
  fertility_rate:'Fertility Rate', infant_mortality:'Infant Mortality',
  population_growth:'Pop. Growth', urbanization:'Urbanization', literacy_rate:'Literacy',
  pop_0_14:'Age 0-14', pop_65_plus:'Age 65+', median_age:'Dependency Ratio',
  refugee_population:'Refugees', military_expenditure_usd:'Military Spending',
  military_expenditure_pct_gdp:'% of GDP', military_expenditure_pct_govt:'% of Govt Budget',
  area_sq_km:'Area', coastline_km:'Coastline', land_boundaries_km:'Land Borders',
  climate:'Climate', terrain:'Terrain', natural_resources:'Resources', location:'Location',
  government_type:'System', capital:'Capital', independence_date:'Independence',
  legal_system:'Legal System', executive_branch:'Head of State', elections:'Elections',
  constitution:'Constitution', background:'Background', flag_description:'Flag',
};

const FactbookSection = ({ data, tab }) => {
  if (!data || Object.keys(data).length === 0)
    return (
      <div style={{ color:'#444', fontSize:'11px', fontStyle:'italic', marginTop:'10px', lineHeight:1.6 }}>
        {tab} data not yet available for this country.
      </div>
    );

  // Separate numeric and text fields
  const numericFields = [];
  const textFields = [];

  for (const [k, v] of Object.entries(data)) {
    if (v === null || v === undefined) continue;
    const label = LABELS[k] || k.replace(/_/g,' ');
    const formatter = fmt[k];
    if (formatter) {
      numericFields.push({ key:k, label, value: formatter(v) });
    } else if (typeof v === 'string' && v.length > 60) {
      textFields.push({ key:k, label, value:v });
    } else {
      numericFields.push({ key:k, label, value: typeof v==='number' ? v.toLocaleString() : String(v) });
    }
  }

  return (
    <div>
      {numericFields.map(({key, label, value}) =>
        <FactRow key={key} label={label} value={value} />
      )}
      {textFields.map(({key, label, value}) => (
        <div key={key} style={{ marginTop:8, paddingBottom:8, borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ color:'#555', fontSize:'11px', marginBottom:3, textTransform:'uppercase', letterSpacing:'0.4px' }}>{label}</div>
          <div style={{ color:'#888', fontSize:'11px', lineHeight:1.6 }}>{value.substring(0,300)}{value.length>300?'…':''}</div>
        </div>
      ))}
    </div>
  );
};

const MiniMap = ({ entity, displayThreatLevel }) => {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);

  useEffect(() => {
    if (!entity || !containerRef.current || !window.mapboxgl) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const geo = entity.geojson;
    if (!geo?.geometry) return;

    window.mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
    const m = new window.mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/satellite-v9',
      interactive: false, center:[0,20], zoom:1
    });
    mapRef.current = m;

    m.on('load', () => {
      try {
        const bounds = new window.mapboxgl.LngLatBounds();
        const extend = (c) => { if (typeof c[0]==='number') bounds.extend(c); else c.forEach(extend); };
        extend(geo.geometry.coordinates);
        m.fitBounds(bounds, { padding:28, animate:false, maxZoom:7 });
      } catch(e) {}

      m.addSource('country', { type:'geojson', data:{ type:'Feature', geometry:geo.geometry, properties:{} } });
      m.addLayer({ id:'country-fill', type:'fill', source:'country',
        paint:{ 'fill-color':levelColor(displayThreatLevel || entity.threat_level), 'fill-opacity':0.4 } });
      m.addLayer({ id:'country-outline', type:'line', source:'country',
        paint:{ 'line-color':levelColor(displayThreatLevel || entity.threat_level), 'line-width':2 } });

      let centLat=0, centLon=0;
      try {
        const flat=[];
        const collect=(c)=>{ if(typeof c[0]==='number') flat.push(c); else c.forEach(collect); };
        collect(geo.geometry.coordinates);
        centLon=flat.reduce((s,c)=>s+c[0],0)/flat.length;
        centLat=flat.reduce((s,c)=>s+c[1],0)/flat.length;
      } catch(e){}

      const events = mockEvents();
      m.addSource('events', { type:'geojson', data:{ type:'FeatureCollection',
        features: events.map(ev=>({ type:'Feature', id:ev.id,
          properties:{ icon:ev.icon, date:ev.date, domainKey:ev.domainKey },
          geometry:{ type:'Point', coordinates:[centLon+ev.offsetLon, centLat+ev.offsetLat] }
        }))
      }});
      m.addLayer({ id:'event-dots', type:'circle', source:'events',
        paint:{ 'circle-radius':7,
          'circle-color':['match',['get','domainKey'],'D2','#81c784','D3','#00bcd4','D6','#fff176','#c4a563'],
          'circle-stroke-color':'#000', 'circle-stroke-width':1.5 }
      });
      m.addLayer({ id:'event-labels', type:'symbol', source:'events',
        layout:{ 'text-field':['concat',['get','icon'],' ',['get','date']],
          'text-size':9, 'text-anchor':'top', 'text-offset':[0,1.1], 'text-max-width':10 },
        paint:{ 'text-color':'#ffffff', 'text-halo-color':'#000', 'text-halo-width':1.2 }
      });
    });

    return () => { if(mapRef.current){ mapRef.current.remove(); mapRef.current=null; } };
  }, [entity, displayThreatLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!entity?.geojson?.geometry)
    return <div style={{ height:'160px', backgroundColor:'#0a0d14', display:'flex', alignItems:'center',
      justifyContent:'center', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ textAlign:'center', color:'#444', fontSize:'11px' }}>
        <div style={{ fontSize:'20px', marginBottom:'4px' }}>🗺️</div>Map data unavailable
      </div>
    </div>;

  return <div ref={containerRef} style={{ height:'160px', flexShrink:0 }} />;
};

const CountryPanel = ({ entityId, entityName, activeDomains=[], onClose, referenceEntityId=null, referenceEntityName='', referenceAssessments={} }) => {
  const [entity,    setEntity]    = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');
  const [expandedEvent, setExpandedEvent] = useState(null);

  useEffect(() => {
    if (!entityId) return;
    setEntity(null); setActiveTab('Overview'); setExpandedEvent(null);
    axios.get(`${API_BASE}/api/v2/global/entities/${entityId}`)
      .then(r => setEntity(r.data))
      .catch(() => setEntity({ id:entityId, name:entityName, threat_level:'unknown' }))
;
  }, [entityId, entityName]);

  // In reference mode, find the bilateral assessment from reference TO this entity
  if (!entity) return <div style={{ flex:'1 1 300px', maxWidth:'380px', backgroundColor:'#0a0d14', display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ color:'#444', fontSize:12 }}>Loading…</div></div>;

  const bilateralAssessment = referenceEntityId
    ? Object.values(referenceAssessments).find(a => a.source_entity_id === entity?.id)
    : null;
  const displayThreatLevel = bilateralAssessment ? bilateralAssessment.escalation_code : entity?.threat_level;
  const col    = levelColor(displayThreatLevel);

  const events = mockEvents();
  const fb     = entity?.factbook || {};
  const entityDomainKeys = [...new Set(events.map(e => e.domainKey))];


  const flagUrl = entity?.iso2_code
    ? `https://flagcdn.com/w80/${entity.iso2_code}.png`
    : null;
  const statusText = (entity?.current_status || '').replace(/<[^>]+>/g, '');
  const recentEvents = entity?.recent_events || [];

  return (
    <div style={{
      flex:'1 1 300px', maxWidth:'380px', height:'clamp(500px, 70vw, 720px)',
      backgroundColor:'#0a0d14',
      borderLeft:'1px solid rgba(196,165,99,0.15)',
      display:'flex', flexDirection:'column', overflowY:'auto', position:'relative'
    }}>

      {/* Header */}
      <div style={{ padding:'12px 14px 8px',
        borderBottom:'1px solid rgba(255,255,255,0.06)', flexShrink:0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {flagUrl && (
              <img src={flagUrl} alt={entity.name}
                style={{ width:36, height:'auto', borderRadius:3,
                  border:'1px solid rgba(255,255,255,0.1)', flexShrink:0 }}
                onError={e => { e.target.style.display='none'; }}
              />
            )}
            <div>
              <h2 style={{ margin:0, color:'#e8e9ed', fontSize:17,
                fontFamily:'Georgia,serif', fontWeight:'bold', lineHeight:1.2 }}>
                {entity.name}
              </h2>
              <div style={{ color:'#555', fontSize:10, marginTop:2 }}>
                {entity.iso_code} · {(entity.region_name||'').replace(/^R[0-9]+: /,'')}
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
            <span style={{ padding:'3px 8px', borderRadius:4, fontSize:10,
              fontWeight:'bold', textTransform:'uppercase', letterSpacing:'0.4px',
              backgroundColor:col+'22', color:col, border:`1px solid ${col}55` }}>
              {levelLabel(displayThreatLevel)}
            </span>
            <button onClick={onClose} style={{ background:'none', border:'none',
              color:'#555', cursor:'pointer', fontSize:18, padding:'0 2px', lineHeight:1 }}>
              ×
            </button>
          </div>
        </div>
        {entityDomainKeys.length > 0 && (
          <div style={{ display:'flex', gap:4, marginTop:8, flexWrap:'wrap' }}>
            {entityDomainKeys.map(dk => {
              const d = DOMAINS[dk];
              return d ? (
                <span key={dk} style={{ fontSize:10, padding:'1px 6px', borderRadius:3,
                  backgroundColor:d.color+'22', color:d.color, border:`1px solid ${d.color}44` }}>
                  {d.icon} {d.label}
                </span>
              ) : null;
            })}
          </div>
        )}
      </div>

      {/* Mini Map */}
      <div style={{ flexShrink:0 }}>
        <MiniMap entity={entity} displayThreatLevel={displayThreatLevel} />
        <div style={{ display:'flex', gap:4, padding:'5px 10px',
          backgroundColor:'rgba(0,0,0,0.4)', flexWrap:'wrap',
          borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
          {[['🏛️','Capital'],['⚓','Ports'],['🎖️','Military'],['⚡','Energy'],['🛣️','Transit']].map(([icon,label]) => (
            <button key={label} title="Infrastructure overlay — coming soon"
              style={{ display:'flex', alignItems:'center', gap:3,
                padding:'2px 7px', borderRadius:4, fontSize:9,
                backgroundColor:'rgba(255,255,255,0.03)',
                border:'1px solid rgba(255,255,255,0.07)',
                color:'#3a3a3a', cursor:'not-allowed' }}>
              {icon} {label}
            </button>
          ))}
        </div>
      </div>

      {/* Current Status */}
      {statusText.length > 0 && (
        <div style={{ padding:'10px 14px',
          borderBottom:'1px solid rgba(255,255,255,0.05)', flexShrink:0 }}>
          <div style={{ color:'#555', fontSize:9, fontWeight:'bold',
            letterSpacing:'0.5px', marginBottom:5 }}>CURRENT STATUS</div>
          <p style={{ color:'#8a8b8f', fontSize:12, lineHeight:1.7, margin:0 }}>
            {statusText.substring(0, 400)}{statusText.length > 400 ? '…' : ''}
          </p>
        </div>
      )}

      {/* Bilateral Context */}
      {referenceEntityId && (
        <div style={{ padding:'8px 14px',
          borderBottom:'1px solid rgba(255,255,255,0.05)', flexShrink:0 }}>
          {bilateralAssessment ? (
            <div style={{ padding:'8px 10px', backgroundColor:col+'11',
              border:`1px solid ${col}33`, borderRadius:5 }}>
              <div style={{ color:col, fontSize:9, fontWeight:'bold',
                letterSpacing:'0.4px', marginBottom:3 }}>
                FROM {(referenceEntityName||'').toUpperCase()}&apos;S PERSPECTIVE
              </div>
              <div style={{ color:col, fontSize:12, fontWeight:'bold', marginBottom:2 }}>
                {levelLabel(bilateralAssessment.escalation_code)}
                <span style={{ color:'#555', fontSize:10, fontWeight:'normal', marginLeft:6 }}>
                  ({bilateralAssessment.domain})
                </span>
              </div>
              {bilateralAssessment.assessment && (
                <div style={{ color:'#777', fontSize:11, lineHeight:1.5 }}>
                  {bilateralAssessment.assessment}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding:'6px 10px', backgroundColor:'rgba(45,106,79,0.08)',
              border:'1px solid rgba(45,106,79,0.2)', borderRadius:5 }}>
              <div style={{ color:'#444', fontSize:9 }}>
                FROM {(referenceEntityName||'').toUpperCase()}&apos;S PERSPECTIVE
              </div>
              <div style={{ color:'#2d6a4f', fontSize:12, fontWeight:'bold' }}>Peace</div>
              <div style={{ color:'#444', fontSize:11 }}>No active bilateral assessment</div>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display:'flex', borderBottom:'1px solid rgba(196,165,99,0.15)',
        flexShrink:0 }}>
        {['Events','Details','Intelligence'].map(t => (
          <button key={t}
            onClick={() => setActiveTab(t === 'Details' ? 'Details:Overview' : t)}
            style={{
              flex:1, padding:'9px 4px', background:'transparent', border:'none',
              borderBottom: (t === 'Details' ? activeTab.startsWith('Details') : activeTab === t)
                ? '2px solid #c4a563' : '2px solid transparent',
              color: (t === 'Details' ? activeTab.startsWith('Details') : activeTab === t)
                ? '#c4a563' : '#555',
              cursor:'pointer', fontSize:11, fontWeight:'bold',
              fontFamily:'inherit', letterSpacing:'0.3px'
            }}>{t}</button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ flex:1, overflowY:'auto', padding:'10px 14px' }}>

        {/* EVENTS TAB */}
        {activeTab === 'Events' && (
          <div>
            {recentEvents.length === 0 ? (
              <div style={{ color:'#444', fontSize:11, fontStyle:'italic',
                textAlign:'center', padding:'24px 0' }}>
                No recent events recorded for this region
              </div>
            ) : recentEvents.map((ev, i) => {
              const dc = ev.pmesii_category === 'Military' ? '#ff5555' :
                ev.pmesii_category === 'Economic' ? '#c4a563' :
                ev.pmesii_category === 'Political' ? '#66aaff' :
                ev.pmesii_category === 'Information' ? '#fff176' : '#81c784';
              return (
                <div key={ev.id || i}
                  onClick={() => setExpandedEvent(expandedEvent === i ? null : i)}
                  style={{ padding:'8px 0',
                    borderBottom:'1px solid rgba(255,255,255,0.05)', cursor:'pointer' }}>
                  <div style={{ display:'flex', gap:7, alignItems:'flex-start' }}>
                    <div style={{ width:3, flexShrink:0, alignSelf:'stretch',
                      backgroundColor:dc, borderRadius:2, minHeight:16 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ color:'#c8c9cd', fontSize:12, lineHeight:1.4,
                        fontWeight: expandedEvent===i ? 'bold' : 'normal' }}>
                        {ev.title}
                      </div>
                      <div style={{ display:'flex', gap:8, marginTop:3 }}>
                        <span style={{ color:'#444', fontSize:10 }}>
                          {new Date(ev.event_date).toLocaleDateString('en-US',
                            {month:'short', day:'numeric', timeZone:'UTC'})}
                        </span>
                        <span style={{ color:dc, fontSize:10 }}>{ev.pmesii_category}</span>
                        {ev.confidence_level && (
                          <span style={{ color:'#555', fontSize:10 }}>
                            CI {Math.round(ev.confidence_level * 100)}%
                          </span>
                        )}
                      </div>
                      {expandedEvent === i && ev.description && (
                        <p style={{ color:'#666', fontSize:11, lineHeight:1.6,
                          margin:'6px 0 0', fontStyle:'italic' }}>
                          {ev.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div style={{ color:'#333', fontSize:10, textAlign:'center',
              padding:'10px 0', fontStyle:'italic' }}>
              Events sourced from Daily Briefings · CI = Confidence Interval
            </div>
          </div>
        )}

        {/* DETAILS TAB */}
        {activeTab.startsWith('Details') && (
          <div>
            <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:12 }}>
              {['Overview','Demographics','Economy','Military','Government','Geography'].map(t => (
                <button key={t} onClick={() => setActiveTab('Details:'+t)}
                  style={{ padding:'3px 8px', borderRadius:4,
                    backgroundColor: activeTab==='Details:'+t
                      ? 'rgba(196,165,99,0.15)' : 'transparent',
                    border:`1px solid ${activeTab==='Details:'+t
                      ? '#c4a563' : 'rgba(255,255,255,0.1)'}`,
                    color: activeTab==='Details:'+t ? '#c4a563' : '#555',
                    cursor:'pointer', fontSize:10, fontWeight:'bold' }}>
                  {t}
                </button>
              ))}
            </div>
            <FactbookSection
              data={fb[activeTab.replace('Details:','').toLowerCase()]}
              tab={activeTab.replace('Details:','')}
            />
          </div>
        )}

        {/* INTELLIGENCE TAB */}
        {activeTab === 'Intelligence' && (
          <div>
            <div style={{ color:'#555', fontSize:9, fontWeight:'bold',
              letterSpacing:'0.5px', marginBottom:8 }}>GLOBAL ESCALATION LEVEL</div>
            <FactRow label="Status" value={levelLabel(entity.threat_level)}
              color={levelColor(entity.threat_level)} />
            <FactRow label="ISO Code" value={entity.iso_code} />
            <FactRow label="COCOM Region"
              value={(entity.region_name||'').replace(/^R[0-9]+: /,'')} />
            {entity.population && (
              <FactRow label="Population"
                value={(entity.population/1e6).toFixed(1)+' M'} />
            )}
            {entity.gdp_usd_millions && (
              <FactRow label="GDP"
                value={'$'+(entity.gdp_usd_millions/1e6).toFixed(2)+' T'} />
            )}
            {fb.military && Object.keys(fb.military).length > 0 && (
              <>
                <div style={{ color:'#555', fontSize:9, fontWeight:'bold',
                  letterSpacing:'0.5px', marginTop:12, marginBottom:8 }}>MILITARY</div>
                <FactbookSection data={fb.military} tab="Military" />
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default CountryPanel;
